import {
  removeNull,
  resetAttribute,
  getRemoveRepeat
} from '@/utils/handleData.js'

const DEFAULT_CHECKING_TWICE = '每天一次'
const DEFAULT_CHECKING_TYPES = [
  '电子围栏定位',
  '门禁打卡',
  '考勤机打卡',
  'web手动打卡',
  '班前教育打卡'
]

function parseCheckingType(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (raw == null || raw === '') return []
  let text = String(raw).trim()
  if (text.startsWith('{') && text.endsWith('}')) {
    text = text.slice(1, -1)
  }
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch (e) {
      /* ignore */
    }
  }
  return text
    .replace(/\s*/g, '')
    .split(',')
    .filter(Boolean)
}

function ensureGroupDefaults(data) {
  const payload = Object.assign({}, data || {})
  if (!payload.checkingTwice) {
    payload.checkingTwice = DEFAULT_CHECKING_TWICE
  }
  const types = parseCheckingType(payload.checkingType)
  payload.checkingType = types.length ? types : DEFAULT_CHECKING_TYPES.slice()
  return payload
}

function parseGeoJson(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function geoCoordinates(geo) {
  if (!geo || geo.coordinates == null) return []
  const c = geo.coordinates
  if (geo.type === 'Point') return [c]
  if (geo.type === 'LineString') return c
  if (geo.type === 'Polygon') return stripClosingDuplicate(c[0] || [])
  // 无 type 时：长度为 2 且元素为数字 → 视为单点 [lng, lat]
  if (
    Array.isArray(c) &&
    c.length === 2 &&
    typeof c[0] === 'number' &&
    typeof c[1] === 'number'
  ) {
    return [c]
  }
  if (Array.isArray(c[0]) && typeof c[0][0] === 'number') {
    return stripClosingDuplicate(c)
  }
  if (Array.isArray(c[0]) && Array.isArray(c[0][0])) {
    return stripClosingDuplicate(c[0])
  }
  return Array.isArray(c) ? c : []
}

function sameLngLat(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return false
  return Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1])
}

/** Polygon 闭环会重复首点，编辑回显时去掉，避免选项多出重复点 */
function stripClosingDuplicate(coords) {
  if (!coords || coords.length < 2) return coords || []
  if (sameLngLat(coords[0], coords[coords.length - 1])) {
    return coords.slice(0, -1)
  }
  return coords
}

function areaRings(geo) {
  if (!geo || geo.coordinates == null) return []
  const c = geo.coordinates
  if (geo.type === 'Polygon') return c
  if (geo.type === 'MultiPolygon') {
    const rings = []
    const polys = c || []
    polys.forEach((poly) => {
      const polyRings = poly || []
      polyRings.forEach((ring) => {
        if (ring && ring.length) rings.push(ring)
      })
    })
    return rings
  }
  if (Array.isArray(c[0]) && Array.isArray(c[0][0])) return c
  return [c]
}

export default {
  namespaced: true,
  state: {
    attendanceGroupList: {}, // 某机构考勤组列表
    attendanceGroupInfo: {}, // 某考勤组详情
    attendanceAreaList: [], // 某机构考勤区域列表
    lnglats: [] // 缓存设置考勤区域时添加的点位信息
  },
  actions: {
    // 新增某机构考勤组
    async addAttendanceGroup({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/check/group',
          data: ensureGroupDefaults(data),
          msg: '新增考勤组成功'
        },
        { root: true }
      )
      return res
    },
    // 删除某机构考勤组
    async deleteAttendanceGroup({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/check/group',
          data: { uid: uid },
          msg: '删除考勤组成功'
        },
        { root: true }
      )
      return res
    },
    // 获取某机构考勤组
    async getAttendanceGroupList({ rootState, commit, dispatch }) {
      var queryValues = rootState.queryValues.attendanceGroup
      queryValues.page = rootState.page.attendanceGroup
      queryValues.limit = rootState.limit.attendanceGroup
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/laborclock/checkingbaseinfo',
          data: queryValues
        },
        { root: true }
      )
      // console.log(res)
      if (res.data) {
        commit(
          'baseSet',
          {
            module: 'attendanceSet',
            key: 'attendanceGroupList',
            value: res.data
          },
          { root: true }
        )
      } else {
        commit(
          'baseSet',
          { module: 'attendanceSet', key: 'attendanceGroupList', value: [] },
          { root: true }
        )
      }
    },
    // 获取考勤组详情
    async getAttendanceGroupInfo({ commit, dispatch, rootState }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/laborclock/checkdetail',
          data: { checkingUid: uid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data)
        ? res.data
        : res && res.data
        ? [res.data]
        : []
      const first = rows[0]
      if (!first) {
        const empty = {}
        commit(
          'baseSet',
          {
            module: 'attendanceSet',
            key: 'attendanceGroupInfo',
            value: empty
          },
          { root: true }
        )
        return empty
      }
      const attendanceGroupInfo = {}
      attendanceGroupInfo.uid = first.uid || first.checkingUid || uid
      attendanceGroupInfo.orgUid =
        first.orgUid || first.checkingOrgUid || rootState.user.orgValue
      attendanceGroupInfo.checkingName = first.checkingName || ''
      attendanceGroupInfo.checkingTwice = first.checkingTwice || ''
      attendanceGroupInfo.allowSupplement =
        first.allowSupplement === true ||
        first.allowSupplement === 'true' ||
        first.allowSupplement === '1'
      attendanceGroupInfo.allowMaxNum = first.allowMaxNum || 0
      attendanceGroupInfo.allowAgoDay = first.allowAgoDay || ''
      attendanceGroupInfo.checkingType = parseCheckingType(first.checkingType)
      const handleData = getRemoveRepeat(rows, [
        'checkingOrg',
        'orgUid',
        'fenceUid',
        'gisFenceUid'
      ])
      attendanceGroupInfo.joinOrgUids = handleData.orgUid || []
      attendanceGroupInfo.joinOrgStr = (handleData.checkingOrg || []).join(
        '、\xa0\xa0'
      )
      attendanceGroupInfo.areas =
        handleData.fenceUid && handleData.fenceUid.length
          ? handleData.fenceUid
          : handleData.gisFenceUid || []
      commit(
        'baseSet',
        {
          module: 'attendanceSet',
          key: 'attendanceGroupInfo',
          value: attendanceGroupInfo
        },
        { root: true }
      )
      return attendanceGroupInfo
    },
    // 编辑某机构考勤组
    async editAttendanceGroup({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/check/group',
          data: ensureGroupDefaults(data),
          msg: '编辑考勤组成功'
        },
        { root: true }
      )
      return res
    },
    // 新增某机构考勤区域
    async addAttendanceArea({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/gis/fence',
          data: data,
          msg: '新增考勤区域成功'
        },
        { root: true }
      )
      return res
    },
    // 删除某机构考勤区域
    async deleteAttendanceArea({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/gis/fence',
          data: { uid: uid },
          msg: '删除考勤区域成功'
        },
        { root: true }
      )
      return res
    },
    // 查询某机构考勤区域列表
    async getAttendanceAreaList({ rootState, commit, dispatch }, params) {
      const query =
        (rootState.queryValues && rootState.queryValues.attendanceArea) || {}
      let uid = rootState.user.orgValue
      let name = ''
      let forOptions = false
      if (typeof params === 'string') {
        uid = params
        forOptions = true
      } else if (params && typeof params === 'object') {
        uid = params.organUid || params.orgUid || uid
        name = params.name || ''
        forOptions = params.all === true
      }
      const step = rootState.currentStep && rootState.currentStep.step
      if (!forOptions && !name && step === 'attendanceAreaList') {
        name = query.name || ''
      }
      if (typeof uid !== 'string') {
        uid = rootState.user.orgValue
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/gis/fence/list',
          data: {
            organUid: uid,
            name: name || undefined,
            fenceType: 'work_attendance'
          }
        },
        { root: true }
      )
      // console.log(res)
      const rows = Array.isArray(res && res.data) ? res.data : []
      rows.forEach((item) => {
        const geo = parseGeoJson(item.data)
        const areaGeo = parseGeoJson(item.area)
        const dataCoords = geoCoordinates(geo)
        if (item.dataType === 'point') {
          item.areaType = '单点周边圆形区域范围'
          item.coordinates = dataCoords.length ? [dataCoords[0]] : []
        } else if (item.dataType === 'line') {
          item.areaType = '多点连线两侧区域范围'
          item.coordinates = dataCoords
        } else if (item.dataType === 'area') {
          item.areaType = '多点连线封闭区域范围'
          item.fenceBuffer = '/'
          item.coordinates = dataCoords
        } else {
          item.coordinates = dataCoords
        }
        item.areaCoordinates = areaRings(areaGeo)
        item.lnglats = []
        item.lnglatOptions = []
        const coords = stripClosingDuplicate(item.coordinates || [])
        item.coordinates = coords
        const seen = {}
        coords.forEach((element, index) => {
          if (!element) return
          // 只接受 [lng, lat]，避免把经度/纬度拆成两个选项
          if (!Array.isArray(element) || element.length < 2) return
          const lng = Number(element[0])
          const lat = Number(element[1])
          if (Number.isNaN(lng) || Number.isNaN(lat)) return
          const value = lng + ',' + lat
          if (seen[value]) return
          seen[value] = true
          item.lnglats.push(value)
          item.lnglatOptions.push({
            label: '基准点' + (index + 1),
            value: value
          })
        })
        // 选项 label 按去重后顺序重排
        item.lnglatOptions.forEach((opt, index) => {
          opt.label = '基准点' + (index + 1)
        })
        let latSum = 0
        let lngSum = 0
        let pointNum = 0
        item.polygons = []
        const rings = item.areaCoordinates || []
        rings.forEach((element) => {
          if (item.dataType === 'area' || item.dataType === 'line') {
            item.polygons.push({ path: element })
          }
          const pts = element || []
          pts.forEach((lnglat) => {
            lngSum += lnglat[0]
            latSum += lnglat[1]
            pointNum++
          })
        })
        item.center = pointNum
          ? [lngSum / pointNum, latSum / pointNum]
          : [0, 0]
      })
      const attendanceAreaList = resetAttribute(rows, 'fenceName', 'uid')
      commit(
        'baseSet',
        {
          module: 'attendanceSet',
          key: 'attendanceAreaList',
          value: attendanceAreaList
        },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'attendanceSet', key: 'lnglats', value: [] },
        { root: true }
      )
    },
    // 编辑某机构考勤区域
    async editAttendanceArea({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/gis/fence',
          data: data,
          msg: '编辑考勤区域成功'
        },
        { root: true }
      )
      return res
    },
    // 查询考勤班次类型
    async getCheckingTwice({ commit, dispatch }) {
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/query/laborclock/checktwice' },
        { root: true }
      )
      // console.log(res)
      if (res.data) {
        const checkingTwice = resetAttribute(res.data, 'noteInfo', 'enumType')
        commit(
          'dictionarySet',
          { key: 'checkingTwice', value: checkingTwice },
          { root: true }
        )
      }
    }
  }
}
